import { useState, useCallback, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";

import { AuthContext } from "../context/authContext";

export const useHttpClient = () => {
  const auth = useContext(AuthContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>(null);
  const [errorTitle, setErrorTitle] = useState<string>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [zeroResults, setZeroResults] = useState<boolean>(false);

  const activeHttpRequests = useRef([]);

  const sendRequest = useCallback(
    async (
      url: string,
      method: string = "GET",
      body: BodyInit = null,
      headers: HeadersInit = {},
      successMessage: boolean = false,
      zeroResultsKey: string = "items",
      pollOnTimeout: boolean = false
    ) => {
      setIsLoading(true);
      const httpAbortCtrl = new AbortController();
      activeHttpRequests.current.push(httpAbortCtrl);

      // // SIMULATE TIMEOUT - Abort the actual request after a short delay
      // const shouldSimulateTimeout =
      //   url.includes("/chats/lead-qualification/") && pollOnTimeout;
      // let timeoutId: NodeJS.Timeout | null = null;

      // if (shouldSimulateTimeout) {
      //   timeoutId = setTimeout(() => {
      //     console.log("Simulating timeout - aborting request");
      //     httpAbortCtrl.abort(); // This will cause the fetch to throw an AbortError
      //   }, 100); // Abort after 100ms to simulate a fast timeout
      // }

      try {
        const response = await fetch(url, {
          method,
          body,
          headers,
          signal: httpAbortCtrl.signal,
        });

        // // Clear timeout if request succeeded
        // if (timeoutId) {
        //   clearTimeout(timeoutId);
        // }

        var responseData = await response.json();

        activeHttpRequests.current = activeHttpRequests.current.filter(
          (reqCtrl) => {
            return reqCtrl !== httpAbortCtrl;
          }
        );

        if (!response.ok) {
          if (responseData?.shouldLogout as boolean) {
            auth.logout();
          }

          throw new Error(responseData?.message);
        }

        if (successMessage) {
          setSuccess(true);
        }

        if (
          responseData?.[zeroResultsKey] &&
          responseData?.[zeroResultsKey]?.length === 0
        ) {
          setZeroResults(true);
        }

        setIsLoading(false);
        localStorage.setItem("connectionStatus", "ONLINE");

        return responseData;
      } catch (err: any) {
        // // Clear timeout if it exists and request failed for other reasons
        // if (timeoutId) {
        //   clearTimeout(timeoutId);
        // }

        // console.log(
        //   "HTTP Error caught:",
        //   err.message,
        //   "Error name:",
        //   err.name,
        //   "Error code:",
        //   err.code
        // );

        // Check if this is a timeout-related error
        const isTimeoutError =
          err.name === "AbortError" ||
          err.message?.toLowerCase()?.includes("timeout") ||
          err.message?.toLowerCase()?.includes("network timeout") ||
          err.message?.toLowerCase()?.includes("request timeout") ||
          err.code === "TIMEOUT" ||
          err.code === "NETWORK_TIMEOUT";

        // console.log(
        //   "Is timeout error:",
        //   isTimeoutError,
        //   "Poll on timeout:",
        //   pollOnTimeout
        // );

        // If pollOnTimeout is enabled and this is a timeout error, return timeout flag
        if (
          pollOnTimeout
          // && isTimeoutError
        ) {
          console.log("Returning timeout response instead of throwing error");
          // setIsLoading(false);
          return { reqTimeout: true };
        }

        setError(err.message || "Algo deu errado, por favor tente novamente.");
        setErrorTitle(responseData?.title || "Erro");
        if (
          err.message?.toLowerCase()?.includes("fetch") ||
          err.message?.toLowerCase()?.includes("event")
        ) {
          localStorage.setItem("connectionStatus", "OFFLINE");
        }
        setIsLoading(false);
        throw err;
      }
    },
    []
  );

  useEffect(() => {
    return () => {
      activeHttpRequests.current.forEach((abortCtrl) => {
        abortCtrl.abort();
      });
    };
  }, []);

  const clearError = (needPush: boolean = false) => {
    setError(null);
    if (needPush) {
      history.push("/");
    }
  };

  const clearSuccess = () => {
    setSuccess(false);
  };

  const clearZeroResults = useCallback(() => {
    setZeroResults(false);
  }, []);

  return {
    isLoading,
    error,
    errorTitle,
    success,
    sendRequest,
    clearError,
    clearSuccess,
    zeroResults,
    clearZeroResults,
  };
};
